import {
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputAdornment,
    Radio,
    RadioGroup,
    Typography
} from "@material-ui/core";
import {CashReservesPolicyMet, SocItem} from "../../../domain/SocItem";
import {SectionProps} from "../SectionProps";
import {Controller, useWatch} from "react-hook-form";
import {TextField} from "@ofqual/portal-components/components/TextField";
import {fieldLabels} from "../fieldLabels";
import {Error} from "../../common/Error";

export const QuestionThreeSection: React.FC<SectionProps<SocItem>> = (
    sectionProps
) => {
    const {control, disabled, errors} = sectionProps;


    const {cashReservesPolicy} = useWatch<SocItem>({
        control,
        name: ["cashReservesPolicy"],
    });

    return (
        <>
            <Grid item xs={12}>
                <Typography variant="body1">
                    Condition A5.4 states an awarding organisation must ensure that it will have available sufficient
                    financial resources and facilities to enable it to develop, deliver and award qualifications in a
                    way that complies with its Conditions of Recognition until at least the time by which every Learner
                    for a qualification it makes available has had the opportunity to complete that qualification. This
                    means that an AO should ensure it always has the appropriate level of financial resources to protect
                    students / apprentices. We want to understand <b>how</b> you determine the `sufficient` level of financial
                    resources.
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <Controller
                    control={control}
                    name="cashReservesPolicy"
                    rules={{validate: (value) => value !== null}}
                    render={(props) => (
                        <FormControl component="fieldset" required disabled={disabled}>
                            <FormLabel
                                id="cashReservesPolicy-label"
                                component="legend"
                                style={{marginBottom: "10px"}}
                            >
                                {fieldLabels.cashReservesPolicy}
                            </FormLabel>
                            <RadioGroup
                                aria-labelledby="cashReservesPolicy-label"
                                name="cashReservesPolicy"
                                data-test="radiogroup-cashReservesPolicy"
                                onChange={(e) => props.onChange(e.target.value === "true")}
                            >
                                <FormControlLabel
                                    value={true}
                                    label={"Yes"}
                                    control={<Radio/>}
                                    data-test="cashReservesPolicyYes"
                                    checked={props.value === true}
                                    style={{marginBottom: "10px"}}
                                />
                                <FormControlLabel
                                    value={false}
                                    label={"No"}
                                    control={<Radio/>}
                                    data-test="cashReservesPolicyNo"
                                    checked={props.value === false}
                                />
                            </RadioGroup>
                        </FormControl>
                    )}
                />
                {errors.cashReservesPolicy && (
                    <>
                        <br></br>
                        <Error data-test="cashReservesPolicy-validation">
                            You must select at least one option
                        </Error>
                    </>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Typography data-test="cashReservesPolicyInformation-typography">
                    If yes, please give a short summary of the policy which includes your target level of cash reserves (if it is easier, you can attach the policy to the message chain). If no, please explain how you ensure that you maintain appropriate levels of cash reserves if you don’t have a policy.
                </Typography>
                <br />
                <Controller
                    control={control}
                    rules={{required: true, maxLength: 10000}}
                    name="cashReservesPolicyInformation"
                    as={
                        <TextField
                            label={fieldLabels.ifYesGiveShortSummary}
                            readOnly={disabled}
                            data-test="cashReservesPolicyInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.cashReservesPolicyInformation?.type === "maxLength" && (
                    <Error data-test="cashReservesPolicyInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.cashReservesPolicyInformation?.type === "required" && (
                    <Error data-test="cashReservesPolicyInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>


            <Grid item xs={12}>
                <Controller
                    control={control}
                    name="cashReservesPolicyMet"
                    rules={{validate: (value) => value !== null}}
                    render={(props) => (
                        <FormControl component="fieldset" required disabled={disabled}>
                            <FormLabel
                                id="cashReservesPolicyMet-label"
                                component="legend"
                                style={{marginBottom: "10px"}}
                            >
                                {fieldLabels.CashReservesPolicyMet}
                            </FormLabel>
                            <RadioGroup
                                aria-labelledby="cashReservesPolicyMet-label"
                                name="cashReservesPolicyMet"
                                data-test="radiogroup-CashReservesPolicyMet"
                                onChange={(e) => props.onChange(Number(e.target.value))}
                            >
                                <FormControlLabel
                                    value={CashReservesPolicyMet.cashReservesPolicyMetYes}
                                    label={"Yes"}
                                    control={<Radio/>}
                                    data-test="cashReservesPolicyMetYes"
                                    checked={props.value === CashReservesPolicyMet.cashReservesPolicyMetYes}
                                />
                                <FormControlLabel
                                    value={CashReservesPolicyMet.cashReservesPolicyMetNo}
                                    label={"No"}
                                    control={<Radio/>}
                                    data-test="cashReservesPolicyMetNo"
                                    checked={props.value === CashReservesPolicyMet.cashReservesPolicyMetNo}
                                />
                                <FormControlLabel
                                    value={CashReservesPolicyMet.cashReservesPolicyMetNoReservesPolicy}
                                    label={"No reserves policy"}
                                    control={<Radio/>}
                                    data-test="cashReservesPolicyMetNoReservesPolicy"
                                    checked={props.value === CashReservesPolicyMet.cashReservesPolicyMetNoReservesPolicy}
                                    style={{marginBottom: "10px"}}
                                />
                            </RadioGroup>
                        </FormControl>
                    )}
                />
                {errors.cashReservesPolicyMet && (
                    <>
                        <br></br>
                        <Error data-test="cashReservesPolicyMet-validation">
                            You must select at least one option
                        </Error>
                    </>
                )}
            </Grid>


            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 5000}}
                    name="cashReservesPolicyMetInformation"
                    as={
                        <TextField
                            label={fieldLabels.cashReservesPolicyMetInformation}
                            readOnly={disabled}
                            data-test="cashReservesPolicyMetInformation-field"
                            rows={8}
                            multiline
                        />
                    }
                />
                {errors.cashReservesPolicyMetInformation?.type === "maxLength" && (
                    <Error data-test="cashReservesPolicyMetInformation-validation-length">
                        Must be no more than 5,000 characters
                    </Error>
                )}
                {errors.cashReservesPolicyMetInformation?.type === "required" && (
                    <Error data-test="cashReservesPolicyInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12}>
                <Typography variant="body1">
                    Please provide the following metric:
                </Typography>
            </Grid>


            <Grid item xs={12}>
                <Controller
                    control={control}
                    name="annualFixedCosts"
                    rules={{required: true, min: 0}}
                    as={
                        <TextField
                            type="number"
                            required
                            label={fieldLabels.annualFixedCosts}
                            readOnly={disabled}
                            data-test="annualFixedCosts-field"
                            InputProps={{
                                inputProps: {min: 0, pattern: "[0-9]*"},
                                startAdornment: (
                                    <InputAdornment position="start">£</InputAdornment>
                                ),
                            }}
                        />
                    }
                />
                {errors.annualFixedCosts?.type === "required" && (
                    <Error data-test="annualFixedCosts-required-validation">
                        This field is required
                    </Error>
                )}
                {errors.annualFixedCosts?.type === "min" && (
                    <Error data-test="annualFixedCosts-min-validation">
                        Must be greater than 0
                    </Error>
                )}
                {errors.annualFixedCosts?.type === "pattern" && (
                    <Error data-test="annualFixedCosts-pattern-validation">
                        {errors.annualFixedCosts.message}
                    </Error>
                )}
            </Grid>


            <Grid item xs={12} sm={12}>
                <Typography>
                    {fieldLabels.cashReservesOverrideInformation}
                </Typography>
                <Controller
                    control={control}
                    rules={{required: true, maxLength: 5000}}
                    name="cashReservesOverrideInformation"
                    as={
                        <TextField
                            label={fieldLabels.pleaseEnterYourResponse}
                            readOnly={disabled}
                            data-test="cashReservesOverrideInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.cashReservesOverrideInformation?.type === "maxLength" && (
                    <Error data-test="cashReservesOverrideInformation-validation-length">
                        Must be no more than 5,000 characters
                    </Error>
                )}
                {errors.cashReservesOverrideInformation?.type === "required" && (
                    <Error data-test="cashReservesOverrideInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12}>
                <Controller
                    control={control}
                    name="fundingSources"
                    rules={{validate: (value) => value !== null}}
                    render={(props) => (
                        <FormControl component="fieldset" required disabled={disabled}>
                            <FormLabel
                                id="fundingSources-label"
                                component="legend"
                                style={{marginBottom: "10px"}}
                            >
                                {fieldLabels.fundingSources}
                            </FormLabel>
                            <RadioGroup
                                aria-labelledby="fundingSources-label"
                                name="fundingSources"
                                data-test="radiogroup-fundingSources"
                                onChange={(e) => props.onChange(e.target.value === "true")}
                            >
                                <FormControlLabel
                                    value={true}
                                    label={"Yes"}
                                    control={<Radio/>}
                                    data-test="fundingSourcesYes"
                                    checked={props.value === true}
                                    style={{marginBottom: "10px"}}
                                />
                                <FormControlLabel
                                    value={false}
                                    label={"No"}
                                    control={<Radio/>}
                                    data-test="fundingSourcesNo"
                                    checked={props.value === false}
                                    style={{marginBottom: "10px"}}
                                />
                            </RadioGroup>
                        </FormControl>
                    )}
                />
                {errors.fundingSources && (
                    <>
                        <br></br>
                        <Error data-test="fundingSources-validation">
                            You must select at least one option
                        </Error>
                    </>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{required: true, maxLength: 5000}}
                    name="fundingSourcesInformation"
                    as={
                        <TextField
                            label={fieldLabels.fundingSourcesInformation}
                            readOnly={disabled}
                            data-test="fundingSourcesInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.fundingSourcesInformation?.type === "maxLength" && (
                    <Error data-test="fundingSourcesInformation-validation-length">
                        Must be no more than 5,000 characters
                    </Error>
                )}
                {errors.fundingSourcesInformation?.type === "required" && (
                    <Error data-test="fundingSourcesInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>
        </>
    );
};
